// Types for our data
export interface DataPoint {
  timestamp: Date
  woundInfectionRiskValues: number[]
  days: number[]
  healthStatus: 'green' | 'yellow' | 'red'
  vitalSigns: {
    temperature: number
  }
}
// Generate days for wound infection risk data (0 to 15 days)
const generateDays = (): number[] => {
  const days: number[] = []
  for (let i = 0; i <= 15; i++) {
    days.push(i)
  }
  return days
}
// Function to generate a single wound infection risk value set
const generateWoundInfectionRiskValues = (
  baselineValues: number[],
  deteriorationFactor: number,
): number[] => {
  return baselineValues.map((value) => {
    // Add some random noise and apply deterioration factor
    const noise = (Math.random() - 0.5) * 3
    const deterioration = value * deteriorationFactor
    return Math.max(0, Math.min(100, value + noise + deterioration))
  })
}
// Determine health status based on current wound infection risk
const determineHealthStatus = (
  currentRiskValue: number,
): 'green' | 'yellow' | 'red' => {
  if (currentRiskValue < 30) return 'green' // Baseline
  if (currentRiskValue < 60) return 'yellow' // Inflamed
  return 'red' // Infection Warning
}
// Generate vital signs based on health status
const generateVitalSigns = (healthStatus: 'green' | 'yellow' | 'red') => {
  // Base values for a healthy patient
  let temperature = 98.6 + (Math.random() - 0.5) * 0.3
  // Adjust based on health status
  if (healthStatus === 'yellow') {
    temperature += 0.5
  } else if (healthStatus === 'red') {
    temperature += 1.5
  }
  return {
    temperature: Math.round(temperature * 10) / 10,
  }
}
// Main function to generate mock data for a specified number of hours
export const generateMockData = (hours: number): DataPoint[] => {
  const data: DataPoint[] = []
  const days = generateDays()
  // Create baseline wound infection risk values (following the pattern in the image)
  const baselineWoundInfectionRiskValues = [
    15, 10, 30, 25, 35, 50, 65, 75, 75, 50, 40, 25, 15, 10, 10, 10,
  ]
  // Starting time (48 hours ago)
  const startTime = new Date()
  startTime.setHours(startTime.getHours() - hours)
  for (let i = 0; i < hours; i++) {
    const timestamp = new Date(startTime)
    timestamp.setHours(timestamp.getHours() + i)
    // Calculate how far along we are in the timeline (0 to 1)
    const timelineProgress = i / hours
    // Map the hourly progression to the 16 days of data
    const dayIndex = Math.min(
      Math.floor(timelineProgress * days.length),
      days.length - 1,
    )
    // Get the current wound infection risk values up to the current day
    const currentWoundInfectionRiskValues =
      baselineWoundInfectionRiskValues.slice(0, dayIndex + 1)
    // Pad with zeros if needed to maintain array length
    while (
      currentWoundInfectionRiskValues.length <
      baselineWoundInfectionRiskValues.length
    ) {
      currentWoundInfectionRiskValues.push(0)
    }
    // Determine health status based on the CURRENT day's risk value, not the maximum
    const currentDayRiskValue = baselineWoundInfectionRiskValues[dayIndex]
    const healthStatus = determineHealthStatus(currentDayRiskValue)
    const dataPoint: DataPoint = {
      timestamp,
      days: days.slice(0, dayIndex + 1),
      woundInfectionRiskValues: currentWoundInfectionRiskValues,
      healthStatus,
      vitalSigns: generateVitalSigns(healthStatus),
    }
    data.push(dataPoint)
  }
  return data
}
